import { DOCUMENT, NgIf } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, AfterViewInit, Renderer2, ViewEncapsulation, HostListener, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { FuseConfig, FuseConfigService } from '@fuse/services/config';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { CommandPaletteComponent } from '@shared/components/command-palette/command-palette.component';
import { SharedComponentModule } from '@shared/components/shared-component.module';
import { FusePlatformService } from '@fuse/services/platform';
import { FUSE_VERSION } from '@fuse/version';
import { combineLatest, filter, finalize, map, Subject, takeUntil, takeWhile, tap, timer } from 'rxjs';
import { SettingsComponent } from './common/settings/settings.component';
import { EmptyLayoutComponent } from './layouts/empty/empty.component';
import { CenteredLayoutComponent } from './layouts/horizontal/centered/centered.component';
import { EnterpriseLayoutComponent } from './layouts/horizontal/enterprise/enterprise.component';
import { MaterialLayoutComponent } from './layouts/horizontal/material/material.component';
import { ModernLayoutComponent } from './layouts/horizontal/modern/modern.component';
import { ClassicLayoutComponent } from './layouts/vertical/classic/classic.component';
import { ClassyLayoutComponent } from './layouts/vertical/classy/classy.component';
import { CompactLayoutComponent } from './layouts/vertical/compact/compact.component';
import { DenseLayoutComponent } from './layouts/vertical/dense/dense.component';
import { FuturisticLayoutComponent } from './layouts/vertical/futuristic/futuristic.component';
import { ThinLayoutComponent } from './layouts/vertical/thin/thin.component';
import { IAlertConfig } from '@shared/models/alert';
import { AppAlertService } from '@shared/service/app/alert.service';
import { FuseAlertComponent, FuseAlertService } from '@fuse/components/alert';
import { fuseAnimations } from '@fuse/animations';
import { FuseDrawerComponent } from '@fuse/components/drawer';
import { AltaTicketComponent } from 'app/modules/soporte/components/alta-ticket/alta-ticket.component';
import { AuthService } from '@core/auth/auth.service';
import { AuthUtils } from '@core/auth/auth.utils';

import { SearchService } from './common/search/search.service';

@Component({
    selector     : 'layout',
    templateUrl  : './layout.component.html',
    styleUrls    : ['./layout.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone   : true,
    providers: [AppAlertService],
    animations: fuseAnimations,
    imports      : [FuseAlertComponent, FuseDrawerComponent, AltaTicketComponent, NgIf, EmptyLayoutComponent, CenteredLayoutComponent, EnterpriseLayoutComponent, MaterialLayoutComponent, ModernLayoutComponent, ClassicLayoutComponent, ClassyLayoutComponent, CompactLayoutComponent, DenseLayoutComponent, FuturisticLayoutComponent, ThinLayoutComponent, SettingsComponent, MatDialogModule, SharedComponentModule],
})
export class LayoutComponent implements OnInit, AfterViewInit, OnDestroy
{
    config: FuseConfig;
    layout: string;
    scheme: 'dark' | 'light';
    theme: string;
    showAlert = false;
    _alertConfig: IAlertConfig;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        @Inject(DOCUMENT) private _document: any,
        private _renderer2: Renderer2,
        private _router: Router,
        private _fuseConfigService: FuseConfigService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fusePlatformService: FusePlatformService,
        private alert: AppAlertService,
        private _dialog: MatDialog,
        private _authService: AuthService,
        private _searchService: SearchService
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Set the theme and scheme based on the configuration
        combineLatest([
            this._fuseConfigService.config$,
            this._fuseMediaWatcherService.onMediaQueryChange$(['(prefers-color-scheme: dark)', '(prefers-color-scheme: light)']),
        ]).pipe(
            takeUntil(this._unsubscribeAll),
            map(([config, mql]) =>
            {
                const options = {
                    scheme: config.scheme,
                    theme : config.theme,
                };

                // If the scheme is set to 'auto'...
                if ( config.scheme === 'auto' )
                {
                    // Decide the scheme using the media query
                    options.scheme = mql.breakpoints['(prefers-color-scheme: dark)'] ? 'dark' : 'light';
                }

                return options;
            }),
        ).subscribe((options) =>
        {
            // Store the options
            this.scheme = options.scheme;
            this.theme = options.theme;

            // Update the scheme and theme
            this._updateScheme();
            this._updateTheme();
        });

        // Subscribe to config changes
        this._fuseConfigService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config: FuseConfig) =>
            {
                // Store the config
                this.config = config;

                // Update the layout
                this._updateLayout();
            });

        // Subscribe to NavigationEnd event
        this._router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntil(this._unsubscribeAll),
        ).subscribe(() =>
        {
            // Update the layout
            this._updateLayout();
        });

        // Set the app version
        this._renderer2.setAttribute(this._document.querySelector('[ng-version]'), 'fuse-version', FUSE_VERSION);

        // Set the OS name
        this._renderer2.addClass(this._document.body, this._fusePlatformService.osName);
        this.subscribeAlert();
    }

    /**
     * On after view init
     */
    ngAfterViewInit(): void
    {
        if (this.ticketDrawer) {
            // Reemplazar el handler del overlay de Fuse para pasar por solicitarCierre()
            (this.ticketDrawer as any)._handleOverlayClick = (): void => {
                if (this.altaTicketComp) {
                    this.altaTicketComp.solicitarCierre();
                } else {
                    this.ticketDrawer.close();
                }
            };

            // Asegurar que cada vez que se abra, el overlay use el handler personalizado
            this.ticketDrawer.openedChanged
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe((opened: boolean) => {
                    if (opened) {
                        (this.ticketDrawer as any)._handleOverlayClick = (): void => {
                            if (this.altaTicketComp) {
                                this.altaTicketComp.solicitarCierre();
                            } else {
                                this.ticketDrawer.close();
                            }
                        };
                    }
                });
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the selected layout
     */
    private _updateLayout(): void
    {
        // Get the current activated route
        let route = this._activatedRoute;
        while ( route.firstChild )
        {
            route = route.firstChild;
        }

        // 1. Set the layout from the config
        this.layout = this.config.layout;

        // 2. Get the query parameter from the current route and
        // set the layout and save the layout to the config
        const layoutFromQueryParam = route.snapshot.queryParamMap.get('layout');
        if ( layoutFromQueryParam )
        {
            this.layout = layoutFromQueryParam;
            if ( this.config )
            {
                this.config.layout = layoutFromQueryParam;
            }
        }

        // 3. Iterate through the paths and change the layout as we find
        // a config for it.
        //
        // The reason we do this is that there might be empty grouping
        // paths or componentless routes along the path. Because of that,
        // we cannot just assume that the layout configuration will be
        // in the last path's config or in the first path's config.
        //
        // So, we get all the paths that matched starting from root all
        // the way to the current activated route, walk through them one
        // by one and change the layout as we find the layout config. This
        // way, layout configuration can live anywhere within the path and
        // we won't miss it.
        //
        // Also, this will allow overriding the layout in any time so we
        // can have different layouts for different routes.
        const paths = route.pathFromRoot;
        paths.forEach((path) =>
        {
            // Check if there is a 'layout' data
            if ( path.routeConfig && path.routeConfig.data && path.routeConfig.data.layout )
            {
                // Set the layout
                this.layout = path.routeConfig.data.layout;
            }
        });
    }

    /**
     * Update the selected scheme
     *
     * @private
     */
    private _updateScheme(): void
    {
        // Remove class names for all schemes
        this._document.body.classList.remove('light', 'dark');

        // Add class name for the currently selected scheme
        this._document.body.classList.add(this.scheme);
    }

    subscribeAlert() {
        this.alert.emitConfig.subscribe({
          next: (config: IAlertConfig) => {
            this.showAlert = true;
            this._alertConfig = {
              ...config,
              appearance: 'border',
            };
            let time = 2;
            timer(1000, 1000)
              .pipe(
                finalize(() => {
                  this.showAlert = false;
                  this._alertConfig = null;
                }),
                takeWhile(() => time > 0),
                takeUntil(this._unsubscribeAll),
                tap(() => (time -= 1))
              )
              .subscribe();
          },
        });
    }
    

    /**
     * Update the selected theme
     *
     * @private
     */
    private _updateTheme(): void
    {
        // Find the class name for the previously selected theme and remove it
        this._document.body.classList.forEach((className: string) =>
        {
            if ( className.startsWith('theme-') )
            {
                this._document.body.classList.remove(className, className.split('-')[1]);
            }
        });

        // Add class name for the currently selected theme
        this._document.body.classList.add(this.theme);
    }

    @ViewChild('ticketDrawer') ticketDrawer: FuseDrawerComponent;
    @ViewChild('altaTicketComp') altaTicketComp: AltaTicketComponent;

    private _isGuestOrEmptyLayout(): boolean {
        if (this.layout === 'empty') {
            return true;
        }

        const token = this._authService?.accessToken;
        if (!token || AuthUtils.isTokenExpired(token)) {
            return true;
        }

        return false;
    }

    @HostListener('document:keydown.control.k', ['$event'])
    openCommandPalette(event: KeyboardEvent) {
        if (this._isGuestOrEmptyLayout()) {
            return;
        }

        event.preventDefault();

        if (this._dialog.openDialogs.some(d => d.componentInstance instanceof CommandPaletteComponent)) {
            return;
        }

        this._dialog.open(CommandPaletteComponent, {
            width: '600px',
            panelClass: 'custom-dialog-container',
            backdropClass: 'blur-backdrop'
        });
    }

    @HostListener('document:keydown.alt.t', ['$event'])
    toggleTicketDrawer(event: KeyboardEvent) {
        if (this._isGuestOrEmptyLayout()) {
            return;
        }

        event.preventDefault();

        if (this.ticketDrawer) {
            if (this.ticketDrawer.opened && this.altaTicketComp) {
                this.altaTicketComp.solicitarCierre();
            } else {
                this.ticketDrawer.toggle();
            }
        }
    }

    @HostListener('document:keydown.control.f', ['$event'])
    handleCtrlF(event: KeyboardEvent): void {
        if (this._isGuestOrEmptyLayout()) {
            return;
        }

        // Si hay un diálogo o modal abierto, no interceptar
        if (this._dialog.openDialogs.length > 0) {
            return;
        }

        // 1. Selector específico para buscadores integrados en tablas y listados
        const integratedSelector = '[data-search-input="true"], .search-integrated-input, app-grid-filter input, .grid-filter input:not([disabled])';
        const localSearchInput = document.querySelector(integratedSelector) as HTMLInputElement | null;

        // Si existe un buscador integrado en la vista activa (ej. Cobranzas & Anticipos, Entregas Rápidas, etc.)
        if (localSearchInput && localSearchInput.offsetParent !== null) {
            event.preventDefault();
            localSearchInput.focus();
            localSearchInput.select?.();
            return;
        }

        // 2. Si la barra superior del layout ya está abierta en el DOM
        const barInput = document.querySelector('input#barSearchInput, .search-opened input') as HTMLInputElement | null;
        if (barInput && barInput.offsetParent !== null) {
            event.preventDefault();
            barInput.focus();
            barInput.select?.();
            return;
        }

        // 3. Disparar apertura reactiva de la barra superior para los módulos estándar
        this._searchService.triggerFocusSearch();

        // 4. Fallback secundario si hay cualquier input de búsqueda general en pantalla
        const generalSearch = document.querySelector('input[placeholder*="Buscar" i]:not([disabled])') as HTMLInputElement | null;
        if (generalSearch && generalSearch.offsetParent !== null) {
            event.preventDefault();
            generalSearch.focus();
            generalSearch.select?.();
            return;
        }

        // Prevenir comportamiento nativo si estamos en el layout principal
        event.preventDefault();
    }
}
